import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { radii } from '@/theme';
import { Mono, useModeTokens } from '@/common/components/ui';
import { InitialsAvatar } from '@/common/components/signal';
import { marketingColumns } from '@/routes/signal/fixtures';
import { ANCHOR, COLLAB_POINTS } from './content';
import { Eyebrow, Headline, useSectionTop } from './primitives';

const projectCount = marketingColumns.reduce(
  (total, column) => total + column.cards.length,
  0
);

const boardMembers = marketingColumns
  .flatMap((column) => column.cards.map((card) => card.avatar))
  .filter(
    (person, index, people) =>
      people.findIndex(
        (candidate) => candidate.initials === person.initials
      ) === index
  );

const OWNER_NAMES: Record<string, string> = {
  AR: 'Ari Reyes',
  JD: 'Jonah Dey',
  MK: 'Mara Kade',
};

const ADDED_LABELS = ['Added today', 'Added 1d ago', 'Added 3d ago'];

const versionCountFromMeta = (meta: string) =>
  meta.match(/v(\d+)/i)?.[1] ?? '1';

/** A product-faithful board presented as the section's main sales proof. */
export const LandingCollaboration: React.FC = () => {
  const t = useModeTokens();
  const sectionTop = useSectionTop('56px');

  return (
    <Box
      id="lp-boards"
      sx={{ ...ANCHOR, padding: { xs: '0 20px 48px', md: '0 40px 60px' } }}
    >
      <Box
        sx={{
          ...sectionTop,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'minmax(0, 0.9fr) minmax(320px, 0.55fr)',
            },
            gap: { xs: '16px', md: '64px' },
            alignItems: 'end',
            marginBottom: { xs: '28px', md: '36px' },
          }}
        >
          <Box>
            <Eyebrow mb="16px" color={t.colors.green}>
              02 / FROM FIRST IDEA TO FINAL MASTER
            </Eyebrow>
            <Headline min={32} max={52} sx={{ maxWidth: '18ch' }}>
              Every project.
              <br />
              <span className="lp-gradient-text">One clear path to done.</span>
            </Headline>
          </Box>
          <Box
            component="p"
            sx={{
              margin: 0,
              fontSize: { xs: '14px', md: '15px' },
              lineHeight: 1.65,
              color: t.text.secondary,
              textWrap: 'pretty',
            }}
          >
            Turn an album, EP or client pipeline into a shared visual workflow.
            Add existing StudioVault projects, shape the stages around your
            process and invite the people moving the work forward.
          </Box>
        </Box>

        <Box
          className="lp-gloss-panel"
          sx={{
            border: `1px solid ${t.borders.medium}`,
            borderRadius: `${radii['2xl']}px`,
            background: `linear-gradient(145deg, ${alpha(
              t.colors.green,
              0.055
            )}, transparent 30%), ${t.surfaces.cardGradientSoft}`,
            boxShadow: t.shadows.modal,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              minHeight: 4,
              background: `linear-gradient(90deg, ${t.colors.green}, ${t.colors.cyan}, ${t.colors.accentFill})`,
              opacity: 0.9,
            }}
          />

          <Box
            sx={{
              padding: { xs: '18px 16px 16px', sm: '20px 22px 18px' },
              borderBottom: `1px solid ${t.borders.subtle}`,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: '14px',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Mono
                size={9}
                weight={600}
                letterSpacing="0.14em"
                color={t.colors.green}
              >
                ILLUSTRATIVE RELEASE BOARD
              </Mono>
              <Box
                sx={{
                  marginTop: '4px',
                  fontWeight: 700,
                  fontSize: { xs: '18px', sm: '20px' },
                  letterSpacing: '-0.025em',
                }}
              >
                Autocannon — LP1
              </Box>
              <Mono size={9.5} sx={{ display: 'block', marginTop: '5px' }}>
                {projectCount} PROJECTS · {marketingColumns.length} STAGES
              </Mono>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Box sx={{ display: 'flex', paddingLeft: '8px' }}>
                {boardMembers.map((person) => (
                  <InitialsAvatar
                    key={person.initials}
                    {...person}
                    size={28}
                    ring={t.surfaces.panel}
                    sx={{ marginLeft: '-8px' }}
                  />
                ))}
              </Box>
              <Box
                component="span"
                sx={{
                  border: `1px solid ${alpha(t.colors.green, 0.4)}`,
                  borderRadius: '5px',
                  background: alpha(t.colors.green, 0.1),
                  color: t.colors.green,
                  padding: '7px 10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                + INVITE MEMBER
              </Box>
            </Box>
          </Box>

          <Box sx={{ padding: { xs: '14px 16px 0', sm: '14px 22px 0' } }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {['⌕  Find a project…', 'ALL OWNERS'].map((label, index) => (
                <Mono
                  key={label}
                  size={9.5}
                  color={index === 0 ? t.text.muted : t.text.secondary}
                  sx={{
                    padding: '7px 10px',
                    border: `1px solid ${t.borders.subtle}`,
                    borderRadius: '5px',
                    background: t.surfaces.trackSoft,
                    minWidth: index === 0 ? { xs: '100%', sm: 180 } : 0,
                  }}
                >
                  {label}
                </Mono>
              ))}
            </Box>
          </Box>

          <Box sx={{ overflowX: 'auto', padding: '14px 16px 18px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(190px, 1fr))',
                gap: '12px',
                alignItems: 'start',
                minWidth: 820,
              }}
            >
              {marketingColumns.map((column) => (
                <Box
                  key={column.name}
                  sx={{
                    border: `1px solid ${t.borders.subtle}`,
                    borderRadius: '7px',
                    background: t.surfaces.trackSoft,
                    padding: '12px 10px 13px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '9px',
                    boxShadow: t.shadows.controlInset,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                      padding: '0 3px 3px',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 7,
                        height: 7,
                        flex: 'none',
                        borderRadius: '999px',
                        background: column.dot,
                        boxShadow: `0 0 0 3px ${alpha(column.dot, 0.12)}`,
                      }}
                    />
                    <Mono
                      size={10.5}
                      weight={600}
                      letterSpacing="0.08em"
                      color={t.text.primary}
                    >
                      {column.name}
                    </Mono>
                    <Mono size={10.5} sx={{ marginLeft: 'auto' }}>
                      {column.count}
                    </Mono>
                    <Mono size={12} color={t.text.faint}>
                      ···
                    </Mono>
                  </Box>

                  {column.cards.map((card, cardIndex) => (
                    <Box
                      key={card.name}
                      className="lp-gloss-inset"
                      sx={{
                        border: `1px solid ${t.borders.default}`,
                        borderRadius: `${radii.md}px`,
                        background: t.surfaces.fill,
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        boxShadow: t.shadows.cardFlat,
                        transition:
                          'transform 160ms ease, border-color 160ms ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          borderColor: alpha(column.dot, 0.5),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 27,
                            height: 27,
                            flex: 'none',
                            borderRadius: '6px',
                            background: alpha(column.dot, 0.12),
                            color: column.dot,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FolderOutlinedIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Mono size={8.5} letterSpacing="0.12em">
                          PROJECT
                        </Mono>
                        <Mono
                          size={12}
                          color={t.text.faint}
                          sx={{ marginLeft: 'auto' }}
                        >
                          ···
                        </Mono>
                      </Box>

                      <Box
                        sx={{
                          fontWeight: 650,
                          fontSize: '13.5px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {card.name}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '7px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            borderRadius: '999px',
                            background: t.surfaces.trackSoft,
                            border: `1px solid ${t.borders.subtle}`,
                            padding: '4px 7px',
                          }}
                        >
                          <LayersOutlinedIcon sx={{ fontSize: 12 }} />
                          <Mono size={8.5} color={t.text.secondary}>
                            {versionCountFromMeta(card.meta)} VERSIONS
                          </Mono>
                        </Box>
                        <Mono
                          size={8.5}
                          sx={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                        >
                          {ADDED_LABELS[cardIndex % ADDED_LABELS.length]}
                        </Mono>
                      </Box>

                      <Box
                        sx={{
                          paddingTop: '8px',
                          borderTop: `1px solid ${t.borders.subtle}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '7px',
                        }}
                      >
                        <InitialsAvatar {...card.avatar} size={20} />
                        <Box
                          sx={{
                            color: t.text.secondary,
                            fontSize: '10.5px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {OWNER_NAMES[card.avatar.initials] ?? 'Project owner'}
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      border: `1px dashed ${t.borders.default}`,
                      borderRadius: '5px',
                      padding: '8px',
                      textAlign: 'center',
                      color: t.text.muted,
                      fontSize: '10.5px',
                      fontWeight: 600,
                    }}
                  >
                    + Add project
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${t.borders.subtle}`,
              padding: '12px 22px',
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'space-between',
              gap: '16px',
            }}
          >
            {[
              'DRAG PROJECTS BETWEEN STAGES',
              'CHANGES SAVE ON DROP',
              'KEYBOARD ACCESSIBLE',
            ].map((label) => (
              <Mono key={label} size={8.5} letterSpacing="0.08em">
                <Box component="span" sx={{ color: t.colors.green, mr: '6px' }}>
                  ✓
                </Box>
                {label}
              </Mono>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: '12px',
            marginTop: '18px',
          }}
        >
          {COLLAB_POINTS.map(([term, rest], index) => (
            <Box
              key={term}
              sx={{
                border: `1px solid ${t.borders.card}`,
                borderRadius: `${radii.lg}px`,
                background: t.surfaces.cardGradientSoft,
                boxShadow: t.shadows.cardSoft,
                padding: '15px 16px',
                display: 'grid',
                gridTemplateColumns: '26px minmax(0, 1fr)',
                gap: '11px',
                fontSize: '12.5px',
                color: t.text.secondary,
                lineHeight: 1.5,
              }}
            >
              <Mono
                size={9}
                weight={700}
                color={t.colors.green}
                sx={{ paddingTop: '2px' }}
              >
                0{index + 1}
              </Mono>
              <Box component="span">
                <Box component="b" sx={{ color: t.text.primary }}>
                  {term}
                </Box>{' '}
                {rest}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LandingCollaboration;
